import { useNavigate } from "react-router-dom";

let _fontInjected = false;
function ensureDmSans() {
    if (_fontInjected || typeof document === "undefined") return;
    _fontInjected = true;
    const links = [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap" },
    ];
    links.forEach((cfg) => {
        const el = document.createElement("link");
        Object.entries(cfg).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
    });
}

export default function GuidePage() {
    ensureDmSans();
    const navigate = useNavigate();

    return (
        <div
            data-testid="guide-page"
            style={{ minHeight: "100vh", background: "var(--pord-bg, #222518)" }}
        >
            <div style={{ maxWidth: 820, margin: "0 auto", padding: "80px 24px 80px" }}>
                {/* Brand + back */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 56 }}>
                    <button
                        onClick={() => navigate("/index")}
                        className="pord-eyebrow"
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    >
                        Pord.
                    </button>
                    <button
                        onClick={() => navigate("/index")}
                        className="pord-label"
                        style={{ background: "none", border: "1px solid rgba(184,212,208,0.18)", borderRadius: 999, padding: "6px 16px", cursor: "pointer", color: "var(--pord-sage)" }}
                    >
                        Back to Index
                    </button>
                </div>

                <h1 className="pord-heading text-3xl sm:text-4xl text-white mb-4">
                    Dashboard Structure
                </h1>
                <p style={styles.caption}>
                    Informational Content and Dashboard. Deep dives on stocks, bonds, banks, and ETFs. Not investment advice.
                </p>

                {/* ======= Card: Index ======= */}
                <Card>
                    <SectionHead first>Index</SectionHead>
                    <ol style={styles.numberedList}>
                        <li><Link onClick={() => navigate("/reference")}>AI — Brain / Body / Adoption-Application</Link></li>
                        <li><Link onClick={() => navigate("/cot")}>Small Cap Reads via CoT Report</Link></li>
                        <li><Link onClick={() => navigate("/treasury")}>Treasury Curve Positioning</Link></li>
                        <li style={{ color: "var(--pord-text-dim)" }}>… tbd. (potentially : reading Banks activities via CoT report)</li>
                        <li style={{ color: "var(--pord-text-dim)" }}>… tbd. …</li>
                    </ol>
                </Card>

                {/* ======= Card: Page 2 ======= */}
                <Card>
                    <SectionHead first>Page 2 · Small Cap Reads via CoT Report</SectionHead>

                    <SubHead>CoT Report</SubHead>
                    <Body>
                        Charting & Percentile are read from CoT Report. The Commitments of Traders (CoT) Report is a publication from the Commodity Futures Trading Commission (CFTC), details the aggregate long and short futures and options positions held by Asset Managers and Leveraged Funds adding context on their market reads through their futures and options positioning.
                    </Body>

                    <SubHead>%OI</SubHead>
                    <Body>OI = total open interest = total open futures contracts of a given category.</Body>

                    {/* Extra breathing room after %OI */}
                    <div style={{ height: 1, margin: "36px 0", background: "linear-gradient(to right, rgba(184,212,208,0.08), transparent 50%)" }} />

                    <Body style={{ marginBottom: 12, color: "var(--pord-teal)" }}>
                        The page is divided into 2 sections.
                    </Body>

                    <GroupHead>Section 1 · RTY</GroupHead>
                    <Body>4 mini cards included:</Body>
                    <ol style={styles.cardList}>
                        <li><Strong>A Summary Card</Strong> — RTY positioning (by Asset Mgr, Leveraged Funds) + Equity Price Overlay</li>
                        <li><Strong>Daily Pricing</Strong> — (RTY — SP500 — NDX)</li>
                        <li><Strong>RTY — Asset Manager</Strong> (long money) Positioning</li>
                        <li><Strong>RTY — Leveraged Fund</Strong> (fast money) Positioning</li>
                    </ol>

                    <GroupHead>Section 2 · SP500</GroupHead>
                    <Body>3 mini cards included:</Body>
                    <ol style={styles.cardList}>
                        <li><Strong>A Summary Card</Strong> — SP500 Positioning by Institutions and Individual (retail) Investors</li>
                        <li><Strong>SPY — Institutional Positioning</Strong> (Asset Manager + Leveraged Fund)</li>
                        <li><Strong>SPY — Individual Investor</Strong> (Retail) Positioning</li>
                    </ol>
                </Card>

                {/* ======= Card: Page 3 ======= */}
                <Card>
                    <SectionHead first>Page 3 · Reading Treasury Curve</SectionHead>
                    <Body style={{ marginBottom: 12, color: "var(--pord-teal)" }}>
                        The page is divided into 2 sections.
                    </Body>

                    <GroupHead>Section 1 · Treasury Curve</GroupHead>
                    <Body>
                        A curve-wide view of Treasury futures positioning showing how exposure is distributed across 2-year, 5-year, 10-year, and long-bond contracts.
                    </Body>

                    <GroupHead>Section 2 · Macro Views</GroupHead>
                    <Body>Two key macro expressions outside outright Treasury futures:</Body>
                    <ol style={styles.cardList}>
                        <li><Strong>DXY</Strong> — U.S. Dollar Index</li>
                        <li><Strong>3M SOFR</Strong> — front-end funding rate</li>
                    </ol>
                </Card>

                {/* ======= Card: Summary Card ======= */}
                <Card>
                    <SectionHead first>Summary Card</SectionHead>

                    <SubHead>RTY</SubHead>
                    <Body>
                        A high-level read on Russell 2000 positioning and price, showing how institutional and leveraged-fund exposures line up with major turning points, regime shifts, and recent small-cap leadership.
                    </Body>

                    <SubHead>SP500</SubHead>
                    <Body>
                        This card shifts to S&P 500 positioning and broad equity leadership. It is meant to show whether large-cap price action is being supported by institutional participation, retail flow, or narrow leadership across market-cap tiers.
                    </Body>

                    <SubHead>Treasury</SubHead>
                    <Body>
                        This card tracks positioning across the four major Treasury futures contracts along the curve, locating where duration exposure is concentrated and whether front-end, belly, or long-end positioning looks extended relative to recent history.
                    </Body>
                </Card>

                {/* ======= Card: Detail Card ======= */}
                <Card>
                    <SectionHead first>Detail Card</SectionHead>
                    <Body style={{ marginBottom: 28 }}>
                        Each card has 3 parts — using <Strong>RTY — Asset Manager (long money) Positioning</Strong> as an example:
                    </Body>

                    <DetailPart num="1" title="Percentile Scale">
                        A focused read on asset-manager positioning at net long / net short. Locating the latest positioning as an exact percentile in the 52-week lookback percentile scale. A quick scan on whether it appears stretched or unusually light (extreme long / short).
                    </DetailPart>

                    <DetailPart num="2" title="Time Series — 10Y trailing">
                        Charting of Russell 2000 asset-manager positioning over time, shown as net %OI against key macro and market events. Use this to see when small-cap exposure swings from net long to net short, and when current positioning looks extreme versus its own history.
                    </DetailPart>

                    <DetailPart num="3" title="Week-o-week Change">
                        Shifts in long and short positions shown as percentage changes in CoT open interest. Use this to see which cohort has been adding or cutting risk most over the latest week.
                    </DetailPart>
                </Card>

                {/* ======= Card: Major Events ======= */}
                <Card>
                    <SectionHead first>Major Events</SectionHead>

                    <Event period="2018-10 to 2018-12" title="Q4 selloff">
                        Pivot window from growth-to-defensive, with the S&P 500 down about 13–14% in the quarter and volatility spiking.
                    </Event>

                    <Event period="2019-09 to 2019-10" title="Repo Stress / QE-front end">
                        Overnight repo and front-end funding rates spiked. QE at the front end to stabilize money-market rates and restore liquidity.
                    </Event>

                    <Event period="2020-02 to 2020-04" title="COVID crash / Policy-driven recovery">
                        Fastest drawdown and equally rapid policy-driven recovery; the S&P 500 fell roughly a third peak-to-trough before retracing into year-end.
                    </Event>

                    <Event period="2020-11 to 2021-03" title="Vaccine / Reflation">
                        Vaccine + fiscal impulse + steepening curve; large-cap index made new highs while cyclicals and small caps outperformed.
                    </Event>

                    <Event period="2022-01 to 2022-06" title="Inflation / Hikes">
                        Start of the hiking cycle; inflation and policy dominate large-cap factor behavior.
                    </Event>

                    <Event period="2023-03 to 2023-05" title="Regional bank stress">
                        Several U.S. mid-sized banks collapsed. Triggered rapid deposit flight, emergency policy support, and a sharp selloff in bank equities.
                    </Event>

                    <Event period="2025-04" title="Tariff Surprise">
                        The tariff-driven risk-off event. S&P 500 dropped almost 5% on April 3 alone and volatility spiking as investors priced in weaker growth and higher inflation risk.
                    </Event>

                    <Event period="2026-01 to 2026-03" title="Small-cap leadership">
                        Small-cap outperformance.
                    </Event>

                    <Event period="2026-03" title="Iran oil shock / energy-led risk-off">
                        The Iran conflict and effective closure of the Strait of Hormuz drove a sharp spike in oil prices, renewed inflationary pressures.
                    </Event>
                </Card>
            </div>
        </div>
    );
}

/* --- Sub-components --- */

function Card({ children }) {
    return (
        <div
            style={{
                marginTop: 28,
                background: "linear-gradient(180deg, rgba(48,60,32,0.25) 0%, rgba(22,28,14,0.45) 100%)",
                border: "1px solid rgba(184,212,208,0.08)",
                borderRadius: 20,
                padding: "36px 32px 40px",
            }}
        >
            {children}
        </div>
    );
}

function SectionHead({ children, first }) {
    return <h2 className="pord-heading text-xl sm:text-2xl text-white" style={{ marginTop: first ? 0 : 48, marginBottom: 20 }}>{children}</h2>;
}

function SubHead({ children }) {
    return <h3 className="pord-heading text-base sm:text-lg text-white" style={{ marginTop: 28, marginBottom: 10 }}>{children}</h3>;
}

function GroupHead({ children }) {
    return (
        <div style={{ marginTop: 32, marginBottom: 16 }}>
            <div className="pord-eyebrow" style={{ fontSize: 11 }}>{children}</div>
            <div style={{ height: 1, marginTop: 6, background: "linear-gradient(to right, rgba(184,212,208,0.12), transparent 60%)" }} />
        </div>
    );
}

function Body({ children, style: s }) {
    return (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14.5, fontWeight: 300, lineHeight: 1.85, color: "rgba(200,205,195,0.82)", marginBottom: 6, ...s }}>
            {children}
        </p>
    );
}

function Strong({ children }) {
    return <span style={{ color: "#ffffff", fontWeight: 400 }}>{children}</span>;
}

function Link({ children, onClick }) {
    return (
        <button onClick={onClick} style={{ background: "none", border: "none", color: "var(--pord-teal)", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit", textDecoration: "underline", textDecorationColor: "rgba(184,212,208,0.3)", textUnderlineOffset: 3 }}>
            {children}
        </button>
    );
}

function DetailPart({ num, title, children }) {
    return (
        <div style={{ marginBottom: 32, paddingLeft: 20, borderLeft: "2px solid rgba(184,212,208,0.12)" }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 300, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--pord-sage)", marginBottom: 6 }}>
                Part {num} · {title}
            </div>
            <Body>{children}</Body>
        </div>
    );
}

function Event({ period, title, children }) {
    return (
        <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 300, color: "var(--pord-sage)", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{period}</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 300, color: "#ffffff" }}>{title}</span>
            </div>
            <Body>{children}</Body>
        </div>
    );
}

const styles = {
    caption: {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        fontWeight: 300,
        color: "var(--pord-text-dim)",
        letterSpacing: "0.02em",
        marginBottom: 0,
    },
    numberedList: {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 15,
        fontWeight: 300,
        lineHeight: 2.2,
        color: "rgba(200,205,195,0.82)",
        paddingLeft: 20,
        listStyleType: "decimal",
    },
    cardList: {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14,
        fontWeight: 300,
        lineHeight: 2,
        color: "rgba(200,205,195,0.82)",
        paddingLeft: 20,
        marginTop: 8,
        listStyleType: "decimal",
    },
};
